import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Sparkles, Ruler, ShoppingBag, ArrowUpRight, Zap } from "lucide-react";
import SizeFinder from "../components/SizeFinder";

const HERO_IMG =
  "https://images.unsplash.com/photo-1697748403348-260ec3169d87?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxmYXNoaW9uJTIwbW9kZWwlMjB3aGl0ZSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg2NjE1NzUyfDA&ixlib=rb-4.1.0&q=85";

const steps = [
  { icon: Ruler, title: "Enter Details", desc: "Height, weight, age, gender and preferred fit." },
  { icon: Zap, title: "Smart Match", desc: "We map your body to your exact size chart instantly." },
  { icon: Sparkles, title: "AI Insight", desc: "Claude explains why the size fits you perfectly." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white tech-grid-bg">
      {/* Nav */}
      <header className="border-b border-[#333] sticky top-0 z-40 backdrop-blur-xl bg-black/80">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Crown className="w-5 h-5 gold" strokeWidth={1.5} />
            <span className="font-display text-xl uppercase tracking-tight">FitFinder</span>
          </div>
          <Link
            to="/admin/login"
            data-testid="nav-admin-link"
            className="overline text-white/60 hover:text-white transition-colors duration-200 flex items-center gap-1"
          >
            Admin <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center gap-2 mb-5">
            <span className="overline text-black bg-gold px-2 py-1">For Shopify Stores</span>
            <span className="overline text-white/40">Premium Quality</span>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl uppercase leading-[0.92]">
            Never guess a
            <br />
            <span className="gold">t-shirt size</span> again
          </h1>
          <p className="text-white/60 text-base mt-6 max-w-md leading-relaxed">
            An AI-backed size finder that turns height and weight into the perfect fit — then
            embeds straight into your Shopify product pages. Fewer returns. Happier customers.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <a
              href="#finder"
              data-testid="hero-try-button"
              className="bg-white text-black font-bold uppercase tracking-wider px-6 py-4 hover:bg-gold transition-colors duration-200 flex items-center gap-2"
            >
              Try It Live <ShoppingBag className="w-4 h-4" />
            </a>
            <Link
              to="/admin/login"
              className="border border-[#333] text-white font-bold uppercase tracking-wider px-6 py-4 hover:border-white transition-colors duration-200 flex items-center gap-2"
            >
              Get Embed Code
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-6 mt-12 border-t border-[#333] pt-8">
            {[
              ["6", "Sizes Mapped"],
              ["AI", "Fit Reasoning"],
              ["1", "Line Embed"],
            ].map(([n, l]) => (
              <div key={l}>
                <div className="font-display text-3xl gold">{n}</div>
                <div className="text-white/40 text-xs uppercase tracking-wider mt-1">{l}</div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          id="finder"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="relative"
        >
          <img
            src={HERO_IMG}
            alt="Fashion models"
            className="absolute -top-8 -right-6 w-40 h-52 object-cover grayscale opacity-30 hidden lg:block border border-[#333]"
          />
          <SizeFinder />
        </motion.div>
      </section>

      {/* How it works */}
      <section className="border-t border-[#333]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <span className="overline text-white/40">How it works</span>
          <h2 className="font-display text-4xl uppercase mt-2 mb-12">Three steps to the right fit</h2>
          <div className="grid md:grid-cols-3 gap-px bg-[#333] border border-[#333]">
            {steps.map((s, i) => (
              <div key={s.title} className="bg-[#0A0A0A] p-8 hover:bg-[#141414] transition-colors duration-200">
                <div className="flex items-center justify-between mb-6">
                  <s.icon className="w-7 h-7 gold" strokeWidth={1.5} />
                  <span className="font-display text-4xl text-white/10">0{i + 1}</span>
                </div>
                <h3 className="font-bold text-lg mb-2">{s.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-[#333]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Crown className="w-4 h-4 gold" strokeWidth={1.5} />
            <span className="overline text-white/40">Premium Fit. Premium Quality.</span>
          </div>
          <span className="text-white/30 text-xs">AI Size Finder for Shopify</span>
        </div>
      </footer>
    </div>
  );
}
